import { useState } from 'react';
import type { Version } from '../api/client';
import { Avatar } from './AppShell';

interface VersionHistoryProps {
  artifactId: string;
  versions: Version[];
  currentVersionId: string;
  onPreview: (versionId: string) => void;
  onRestore: (versionId: string) => void;
  onClose: () => void;
}

function formatVersionDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export default function VersionHistory({
  versions,
  currentVersionId,
  onPreview,
  onRestore,
  onClose,
}: VersionHistoryProps) {
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const handleRestore = async (versionId: string) => {
    setRestoringId(versionId);
    try {
      await onRestore(versionId);
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div
      className="flex flex-col h-full"
      style={{
        width: 320,
        minWidth: 320,
        background: 'var(--rd-surface-panel)',
        borderLeft: '1px solid var(--rd-border-subtle)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between h-[48px] min-h-[48px] px-[var(--sp-4)] border-b"
        style={{ borderColor: 'var(--rd-border-subtle)' }}
      >
        <span
          className="text-[14px] font-semibold"
          style={{ color: 'var(--rd-text-strong)' }}
        >
          Version History
        </span>
        <button
          onClick={onClose}
          className="w-[28px] h-[28px] flex items-center justify-center rounded-[var(--radius-md)] cursor-pointer border-0 bg-transparent transition-colors duration-[120ms]"
          style={{ color: 'var(--rd-text-meta)' }}
          aria-label="Close version history"
          title="Close version history"
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = 'var(--rd-surface-hover)')
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = 'transparent')
          }
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          >
            <path d="M3 3l8 8M11 3l-8 8" />
          </svg>
        </button>
      </div>

      {/* Version list */}
      <div className="flex-1 overflow-y-auto">
        {versions.map((version, index) => {
          const isCurrent = version.id === currentVersionId;
          const isHovered = hoveredId === version.id;
          const isRestoring = restoringId === version.id;
          const isLast = index === versions.length - 1;

          return (
            <div
              key={version.id}
              className="px-[var(--sp-4)] py-[var(--sp-3)] cursor-pointer transition-colors duration-[120ms] rounded-[8px]"
              style={{
                borderBottom: isLast
                  ? 'none'
                  : '1px solid var(--rd-border-subtle)',
                background: isHovered
                  ? 'var(--rd-surface-hover)'
                  : 'transparent',
              }}
              onClick={() => onPreview(version.id)}
              onMouseEnter={() => setHoveredId(version.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {/* Author row */}
              <div className="flex items-center gap-[var(--sp-2)]">
                <Avatar
                  name={version.author.name}
                  avatarUrl={version.author.avatar_url}
                  size={24}
                />
                <span
                  className="text-[13px] font-semibold flex-1 truncate"
                  style={{ color: 'var(--rd-text-medium)' }}
                >
                  {version.author.name}
                </span>
                {isCurrent && (
                  <span
                    className="inline-flex items-center h-[20px] px-[var(--sp-2)] rounded-[var(--radius-full)] text-[13px] font-semibold flex-shrink-0"
                    style={{
                      background: 'var(--rd-surface-success)',
                      color: 'var(--rd-success-text)',
                    }}
                  >
                    Current
                  </span>
                )}
              </div>

              {/* Date */}
              <div
                className="text-[13px] mt-[var(--sp-1)] ml-[32px]"
                style={{ color: 'var(--rd-text-faint)' }}
              >
                {formatVersionDate(version.created_at)}
              </div>

              {/* Change description */}
              {version.change_description && (
                <div
                  className="text-[13px] mt-[var(--sp-1)] ml-[32px]"
                  style={{ color: 'var(--rd-text-subtle)' }}
                >
                  {version.change_description}
                </div>
              )}

              {/* Restore button for non-current versions */}
              {!isCurrent && isHovered && (
                <div className="mt-[var(--sp-2)] ml-[32px]">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRestore(version.id);
                    }}
                    disabled={isRestoring}
                    className="h-[28px] px-[var(--sp-3)] rounded-[var(--radius-md)] text-[13px] font-semibold cursor-pointer border-0 bg-transparent transition-colors duration-[120ms]"
                    style={{
                      color: 'var(--rd-accent)',
                      opacity: isRestoring ? 0.6 : 1,
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = 'var(--rd-surface-highlight)')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'transparent')
                    }
                  >
                    {isRestoring ? 'Restoring...' : 'Restore this version'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
